import { useTranslation } from "react-i18next";
import UnitVehicleAvatar from "../../../../../components/vehicles/UnitVehicleAvatar";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { useIsMonitor } from "../../../../../hooks/useIsMonitor";
// import { useRealtimeDeviceData } from "../../../../../store/admin/vehiclesSocketSelector";
import type { Unit } from "../../../../../store/admin/unitsStore";
import {
  DENSE_ROW,
  ICON_BTN,
  ICON_BTN_DANGER,
  ICON_BTN_ON,
  STATUS_CHIP,
} from "../../../shared";
interface UnitListRowProps {
  unit: Unit;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
    buttonText?: string;
    buttonTextHover?: string;
  };
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  onEdit: (unit: Unit) => void;
  onDetails: (unit: Unit) => void;
  onTrailerClick?: (unit: Unit) => void;
  onDeviceClick?: (unit: Unit) => void;
  onDelete?: (unit: Unit) => void;
  disableCRUD?: boolean;
}

export default function UnitListRow({
  unit,
  themeClasses,
  getStatusColor,
  getStatusText,
  onEdit,
  onDetails,
  onTrailerClick,
  onDeviceClick,
  onDelete,
  disableCRUD: disableCRUDProp,
}: UnitListRowProps) {
  const { t } = useTranslation("adminSections");
  const hasTrailer = !!unit.remolque_id || !!unit.remolque;
  const hasDevice = unit.devices && unit.devices.length > 0;
  const disableCRUD = disableCRUDProp ?? useDisableCRUD();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const plattform = import.meta.env.VITE_THEME;

  // `type` may be a unit type ("truck") or, for yard-assigned vehicles, an
  // array of water types. Translate known unit types, fall back to the raw
  // value so missing keys never render as "units.type.xxx".
  const rawType = unit.type as string | string[] | undefined;
  const unitTypeLabel = Array.isArray(rawType)
    ? rawType.map((v) => t(`units.type.${v}`, { defaultValue: v })).join(", ")
    : rawType
      ? t(`units.type.${rawType}`, { defaultValue: rawType })
      : "";
  const statusName = unit.status?.name || "";
  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText ?? ""} ${themeClasses.buttonTextHover ?? ""}`;
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      <UnitVehicleAvatar
        vehicleId={unit.id}
        name={unit.name}
        pointerModel={unit.pointer_model}
        bodyType={unit.type}
        fallback={
          unit.icon?.includes("http") ? (
            <img
              src={unit.icon}
              alt={unit.name}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div
              className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${getStatusColor(statusName)}`}
            >
              <i className="ri-truck-line text-base"></i>
            </div>
          )
        }
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3
            className={`text-sm font-semibold ${themeClasses.text} truncate`}
            title={unit.name}
          >
            {unit.name}
          </h3>
          <span className={`${STATUS_CHIP} ${getStatusColor(statusName)}`}>
            {getStatusText(statusName)}
          </span>
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {(unitTypeLabel || "-") + " • " + (unit.plate || "-")}
          {Array.isArray(unit.truck_yard_types) &&
            unit.truck_yard_types.length > 0 && (
              <span className="text-cyan-600 dark:text-cyan-300">
                {" • " +
                  unit.truck_yard_types
                    .map((ty) => t(`units.truckYardType.${ty}`, { defaultValue: ty }))
                    .join(", ")}
              </span>
            )}
        </p>
      </div>
      {/* Marca y modelo en una columna de ancho fijo: no salta entre filas. */}
      <div
        className="hidden md:block w-44 shrink-0 min-w-0"
        title={`${t("units.fields.brand")}: ${unit.brand || "-"} · ${t("units.fields.model")}: ${unit.model || "-"}`}
      >
        <p className={`text-sm ${themeClasses.text} truncate`}>
          {unit.brand || "-"}
        </p>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {unit.model || "-"}
        </p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className={neutralBtn}
              onClick={() => onEdit(unit)}
              disabled={disableCRUD}
              aria-label={t("units.actions.edit", "Editar")}
              title={t("units.actions.edit", "Editar")}
            >
              <i className="ri-edit-line"></i>
            </button>
          </CRUDTooltip>
        )}
        {/* Botón conectar/desconectar Device */}
        {plattform === "central-gps" && (
          <CRUDTooltip>
            <button
              onClick={() => onDeviceClick?.(unit)}
              disabled={disableCRUD}
              className={hasDevice ? ICON_BTN_ON : neutralBtn}
              aria-label={
                hasDevice
                  ? t("units.device.disconnect")
                  : t("units.device.connect")
              }
              title={
                hasDevice
                  ? t("units.device.disconnect")
                  : t("units.device.connect")
              }
            >
              <i className="ri-gps-line"></i>
              {!hasDevice && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary-500 rounded-full" />
              )}
            </button>
          </CRUDTooltip>
        )}

        {plattform === "dumax" && (
          <button
            onClick={() => onTrailerClick?.(unit)}
            disabled={disableCRUD}
            className={hasTrailer ? ICON_BTN_ON : neutralBtn}
            aria-label={
              hasTrailer
                ? unit.remolque?.plate || t("units.trailer.connectTrailer")
                : t("units.trailer.connectTrailer")
            }
            title={
              hasTrailer
                ? unit.remolque?.plate
                : t("units.trailer.connectTrailer")
            }
          >
            <i className="ri-caravan-line"></i>
            {!hasTrailer && (
              <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary-500 rounded-full" />
            )}
          </button>
        )}
        <button
          className={neutralBtn}
          onClick={() => onDetails(unit)}
          aria-label={t("units.actions.details", "Detalles")}
          title={t("units.actions.details", "Detalles")}
        >
          <i className="ri-eye-line"></i>
        </button>
        {onDelete && !isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className={ICON_BTN_DANGER}
              onClick={() => onDelete(unit)}
              disabled={disableCRUD}
              aria-label={t("units.actions.delete", "Eliminar")}
              title={t("units.actions.delete", "Eliminar")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </CRUDTooltip>
        )}
      </div>
    </div>
  );
}
